<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-orange" sprite="voicemail" />
          Pharmacy Agents
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-orange" sprite="voicemail" />
            Pharmacy Agents
          </h1>
        </el-col>

        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px"></i>
            Total: {{ totalRecords | number }}
          </el-tag>
        </el-col>
      </el-row>
      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable />
          <el-select
            class="ml-10"
            style="width: 250px"
            v-model="currentPharmacyAgentType"
            placeholder="Select Agent Type"
            @change="changeAgentType"
          >
            <el-option
              v-for="(item, index) in pharmacyAgentTypes"
              :key="index"
              :value="item.value"
              :label="item.label"
            />
          </el-select>
          <el-tooltip
            v-if="selectedTenant.disabled === 'on'"
            placement="top"
            :content="`Account disabled`"
          >
            <el-button
              class="ml-10"
              @click="openCreateModal"
              to
              :disabled="selectedTenant.disabled === 'on'"
              >Add New</el-button
            >
          </el-tooltip>
          <el-button v-else class="ml-10" @click="openCreateModal" to
            >Add New</el-button
          >
        </div>
        <div v-if="tableData.length" class="box right">
          <el-pagination
            small
            background
            layout="prev, pager, next"
            @current-change="pageSet"
            @next-click="pageNext"
            @prev-click="pagePrevious"
            :current-page="page"
            :page-size="recordsPerPage"
            :pager-count="5"
            :total="totalRecords"
          />
        </div>
      </div>
      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          :empty-text="`No ${currentPharmacyAgentType} agents`"
          stripe
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>

    <PioneerRxForm
      v-if="currentPharmacyAgentType === 'Pioneer RX'"
      :context="context"
      :visible="visible"
      :typeOptions="typeOptions"
      :clearForm="clearForm"
      :selectedAgent="selectedAgent"
      :branchOptions="branchOptions"
      @cancelOperation="cancelOperation"
    />
    <BestRxForm
      v-if="currentPharmacyAgentType === 'Best RX'"
      :context="context"
      :visible="visible"
      :typeOptions="typeOptions"
      :clearForm="clearForm"
      :selectedAgent="selectedAgent"
      :branchOptions="branchOptions"
      @cancelOperation="cancelOperation"
    />
    <LibertyRxForm
      v-if="currentPharmacyAgentType === 'Liberty RX'"
      :context="context"
      :visible="visible"
      :typeOptions="typeOptions"
      :clearForm="clearForm"
      :selectedAgent="selectedAgent"
      :branchOptions="branchOptions"
      @cancelOperation="cancelOperation"
    />
    <UnintegratedLocationForm
      v-if="currentPharmacyAgentType === 'Location'"
      :context="context"
      :visible="visible"
      :typeOptions="typeOptions"
      :clearForm="clearForm"
      :selectedAgent="selectedAgent"
      :branchOptions="branchOptions"
      @cancelOperation="cancelOperation"
    />
  </div>
</template>

<script>
import { debounce } from "lodash";
import { phoneNumberFormatter } from "@/globals/globals";
import feathersClient, { apiV2Host } from "../../../feathers/feathers-client";
import PioneerRxForm from "@/views/pages/pharmacy-agents/components/agent-forms/PioneerRxForm.vue";
import BestRxForm from "@/views/pages/pharmacy-agents/components/agent-forms/BestRxForm.vue";
import LibertyRxForm from "@/views/pages/pharmacy-agents/components/agent-forms/LibertyRxForm.vue";
import UnintegratedLocationForm from "@/views/pages/pharmacy-agents/components/agent-forms/UnintegratedLocationForm.vue";

export default {
  name: "PharmacyAgentsTable",

  components: {
    PioneerRxForm,
    LibertyRxForm,
    BestRxForm,
    UnintegratedLocationForm
  },

  data() {
    return {
      search: "",
      totalRecords: 0,
      recordsPerPage: 100,
      page: 1,
      maxPage: 1,
      tableLoading: false,
      tableData: [],
      visibleActionsId: "",
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: ({ row }) => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-orange" sprite="voicemail" />
                </div>
              ];
            }
          }
        },
        {
          field: "id",
          title: "Agent Id",
          sortable: true,
          sortType: "number",
          minWidth: 100,
          showOverflow: true
        },
        {
          field: "name",
          title: "Name",
          sortable: true,
          minWidth: 175,
          showOverflow: true
        },
        {
          field: "npi",
          title: "NPI",
          sortable: true,
          minWidth: 150,
          showOverflow: true
        },
        {
          field: "ncpdp",
          title: "NCPDP",
          sortable: true,
          minWidth: 100,
          showOverflow: false
        },
        {
          field: "baseUrl",
          title: "Base URL",
          sortable: true,
          minWidth: 150,
          showOverflow: true
        },
        {
          field: "callerIdNumber",
          title: "Caller ID Number",
          sortable: true,
          minWidth: 150,
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.numberFormatter(row.callerIdNumber)}</span>];
            }
          }
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="el-icon-edit"
                    onClick={() => this.openEditModal(row.id)}
                    class="table-action-button icon-blue"
                  />
                  {this.selectedTenant.disabled === "on" ? (
                    <el-tooltip content="Account Disabled">
                      <el-button
                        id={"delete-" + row.id}
                        icon="el-icon-delete"
                        onClick={() => this.deleteOperation(row.id)}
                        disabled={true}
                        class="table-action-button icon-red"
                      />
                    </el-tooltip>
                  ) : (
                    <el-button
                      id={"delete-" + row.id}
                      icon="el-icon-delete"
                      onClick={() => this.deleteOperation(row.id)}
                      class="table-action-button icon-red"
                    />
                  )}
                </div>
              ];
            }
          }
        }
      ],
      typeOptions: ["DTMF"],
      clearForm: false,
      visible: false,
      selectedAgent: null,
      context: "",
      branchOptions: [],
      pharmacyAgentTypes: [
        {
          label: "Pioneer RX",
          value: "Pioneer RX"
        },
        {
          label: "Best RX",
          value: "Best RX"
        },
        {
          label: "Liberty RX",
          value: "Liberty RX"
        },
        {
          label: "Location",
          value: "Location"
        }
        // {
        //   label: "QS-1",
        //   value: "QS-1"
        // },
      ],
      currentPharmacyAgentType: null,
      agentTypes: {
        "Pioneer RX": "PioneerRx",
        "Best RX": "BestRx",
        "Liberty RX": "LibertyRx",
        "Location": "Location"
        // "QS-1": "qs-1",
      }
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    }
  },

  watch: {
    selectedTenant() {
      this.findPharmacyAgents();
      this.queryBranches();
    },

    user() {
      this.findPharmacyAgents();
      this.queryBranches();
    },

    page: debounce(function () {
      this.findPharmacyAgents();
    }, 500),

    search: debounce(function () {
      this.findPharmacyAgents();
    }, 500)
  },

  methods: {
    pageNext() {
      if (this.page < this.maxPage) {
        this.page += 1;
      }
    },

    pagePrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },

    pageSet(page) {
      this.page = page;
    },

    //General Form and Table Operations
    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    setupTable() {
      if (
        this.currentPharmacyAgentType === "Pioneer RX" ||
        this.currentPharmacyAgentType === "Liberty RX"
      ) {
        // Check if Pioneer RX columns are already added to avoid duplicates
        const hasBaseUrl = this.tableColumns.some(
          (column) => column.field === "baseUrl"
        );
        const hasCallerIdNumber = this.tableColumns.some(
          (column) => column.field === "callerIdNumber"
        );

        if (!hasBaseUrl && !hasCallerIdNumber) {
          const actionsColumnIndex = this.tableColumns.findIndex(
            (column) => column.field === "actions"
          );
          if (actionsColumnIndex !== -1) {
            this.tableColumns.splice(
              actionsColumnIndex,
              0,
              {
                field: "baseUrl",
                title: "Base URL",
                sortable: true,
                minWidth: 150,
                showOverflow: true
              },
              {
                field: "callerIdNumber",
                title: "Caller ID Number",
                sortable: true,
                minWidth: 150,
                showOverflow: true,
                slots: {
                  default: ({ row }) => {
                    return [
                      <span>{this.numberFormatter(row.callerIdNumber)}</span>
                    ];
                  }
                }
              }
            );
          }
        }
      } else {
        // Remove Pioneer RX columns if they exist
        this.tableColumns = this.tableColumns.filter(
          (column) =>
            column.field !== "baseUrl" && column.field !== "callerIdNumber"
        );
      }
    },

    async findPharmacyAgents() {
      this.tableLoading = true;

      let url = `${apiV2Host}/admin/pharmacy/agents?type=${
        this.agentTypes[this.currentPharmacyAgentType]
      }&tenantId=${this.selectedTenant.id}&recordsPerPage=${
        this.recordsPerPage
      }&page=${this.page}`;

      try {
        if (this.search && this.search.length) {
          url += `&name=${this.search}`;
        }

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`
          }
        });

        const data = await res.json();

        const { records, totalRecords, maxPage } = data;

        this.tableData = [...records];
        this.totalRecords = totalRecords;
        this.maxPage = maxPage;
        this.tableLoading = false;
      } catch (err) {
        this.tableData = [];
        this.tableLoading = false;
      }
    },

    validateId(id) {
      const numericId = parseInt(id, 10);

      if (isNaN(numericId) || numericId < 0) {
        console.error("Invalid ID provided:", id);
        return null;
      }
      return numericId;
    },

    openEditModal(id) {
      const validId = this.validateId(id);
      if (!validId && validId !== 0) {
        this.$notify({
          title: "Error",
          message: "Invalid agent ID",
          type: "error"
        });
        return;
      }

      this.tableLoading = true;
      this.context = "Edit";

      fetch(`${apiV2Host}/admin/pharmacy/agents/${validId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
          Accept: "application/json"
        }
      })
        .then((res) => res.json())
        .then((agent) => {
          this.tableLoading = false;
          this.visible = true;
          this.selectedAgent = agent;
        })
        .catch((err) => {
          this.tableLoading = false;
          this.$notify({
            title: "Error",
            message: "Failed to load agent details",
            type: "error"
          });
        });

      this.clearForm = false;
    },

    openCreateModal() {
      this.context = "Create";
      this.clearForm = false;
      this.visible = true;
    },

    async deleteOperation(id) {
      this.$confirm(
        "Delete an in use agent with breaking results?",
        "warning",
        {
          confirmButtonText: "Yes",
          cancelButtonText: "Cancel"
        }
      )
        .then(async () => {
          if (this.selectedTenant.disabled === "on") {
            this.$notify({
              title: "Account Disabled",
              message: "Your account is disabled.",
              type: "error"
            });
            return;
          }

          this.tableLoading = true;

          try {
            const res = await fetch(
              `${apiV2Host}/admin/pharmacy/agents/${id}`,
              {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
                  Accept: "application/json"
                }
              }
            );
            const response = await res.json();

            this.tableLoading = false;

            if (!res.ok) {
              this.$notify({
                title: response.name,
                message: response.message,
                type: "error"
              });
              return;
            }

            this.findPharmacyAgents();

            this.$notify({
              title: "Success",
              message: "Deleted successfully",
              type: "success"
            });
          } catch (err) {
            this.tableLoading = false;
          }
        })
        .catch(() => true);
    },
    queryBranches() {
      this.branchOptions = [];
      feathersClient
        .service("branches")
        .find({
          query: {
            tenantId: this.selectedTenant.id,
            $sort: { name: 1 }
          }
        })
        .then((res) => {
          this.branchOptions = [...res];
        }).catch( () => {
          this.branchOptions = [];
      });
    },

    cancelOperation(agent) {
      this.clearForm = true;
      this.visible = false;

      if (!agent) return;

      if (this.context === "Create") {
        this.tableData.unshift(agent);
      }

      if (this.context === "Edit") {
        const updateIndex = this.tableData.findIndex(
          (item) => item.id === agent.id
        );

        if (updateIndex >= 0) {
          this.tableData.splice(updateIndex, 1, agent);
        }
      }
    },

    numberFormatter(val) {
      return phoneNumberFormatter(val);
    },

    changeAgentType(val) {
      this.currentPharmacyAgentType = val;
      this.setupTable();
      this.findPharmacyAgents();
    }
  },

  filters: {
    number(value) {
      return !isNaN(value)
        ? new Intl.NumberFormat("en-US").format(value)
        : value;
    }
  },

  mounted() {
    if (this.user) {
      this.currentPharmacyAgentType = this.pharmacyAgentTypes[0].value;
      this.setupTable();
      this.findPharmacyAgents();
      this.queryBranches();
    }
  }
};
</script>
